import htmlReactParser from 'html-react-parser'
import kebabCase from 'lodash/kebabCase'
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { z } from 'zod'

import decodeImage from './utilities/decodeImage'

const customizationsParser = z
  .object({
    clientCode: z.string().nullish(),
    colors: z.record(z.string()).nullish(),
    logo: z.string().nullish(),
    footerLinks: z
      .array(z.object({ title: z.string(), links: z.array(z.object({ title: z.string(), href: z.string() })) }))
      .nullish(),
    translations: z.record(z.any()).nullish(),
  })
  .nullish()

type Customizations = z.infer<typeof customizationsParser>

function parseCustomization(brandingJson: string | null | undefined) {
  const rawBranding = JSON.parse(brandingJson || '{}')

  const customizations: Customizations = customizationsParser.parse(rawBranding)

  // translations
  const translations = customizations?.translations || {}

  // client code
  const clientCode = customizations?.clientCode || null

  // styles
  const styles = [
    ':root, ::backdrop {',
    // colors
    ...Object.entries(customizations?.colors || {}).map(([name, value]) => `--${kebabCase(name)}: ${value};`),
    '}',
  ]
    .filter(Boolean)
    .join('\n')

  // logo
  const logo = (() => {
    const decodedImage = decodeImage(customizations?.logo)
    if (!decodedImage) {
      return null
    }

    if (decodedImage.isSvg) {
      let svgElement: any = htmlReactParser(decodedImage.source.trim(), {
        replace: (n) => {
          if (n.type === 'script') {
            return <></>
          }
        },
      })
      if (Array.isArray(svgElement)) {
        svgElement = svgElement.find((n) => n.type === 'svg')!
      }
      if (svgElement.type !== 'svg') {
        return null
      }
      return (props: any) =>
        React.cloneElement(svgElement, Object.assign({}, svgElement.props, props), svgElement.props.children)
    } else if (decodedImage.srcInBase64) {
      return (props: any) => <img src={`${decodedImage.srcInBase64}`} alt="logo" {...props} />
    }

    // Support for other image formats would go here
    return null
  })()

  return {
    styles,
    clientCode,
    logoUrl: customizations?.logo,
    logoComponent: logo,
    footerLinks: customizations?.footerLinks,
    translations,
  }
}

const defaultCustomizations = parseCustomization((window as any).SKILLFIT_BRANDING)

type ParsedCustomizations = Partial<ReturnType<typeof parseCustomization>>

type BrandingContext = ParsedCustomizations & { switchBranding: (b: string | null | undefined) => void }

const brandingContext = createContext<BrandingContext>({
  switchBranding: () => {
    throw new Error('Missing customization provider')
  },
})

export const BrandingProvider = React.memo(({ children }: React.PropsWithChildren) => {
  const [branding, setBranding] = useState<ParsedCustomizations>()

  const switchBranding = useCallback(
    (brandingJson: string | null | undefined) => {
      if (brandingJson) {
        setBranding(parseCustomization(brandingJson))
      } else {
        setBranding(defaultCustomizations)
      }
    },
    [setBranding]
  )

  const value = useMemo(() => ({ ...branding, switchBranding }), [branding, switchBranding])

  return (
    <brandingContext.Provider value={value}>
      {value.styles ? <style>{value.styles}</style> : null}
      {children}
    </brandingContext.Provider>
  )
})

export const useBranding = () => useContext(brandingContext)
